import {Component, Event, EventEmitter, Fragment, h, Host, Prop, State, Watch} from "@stencil/core";
import {replaceText} from "../../common/i18n-util";
import {_debug} from "../../common/debugger/debugger";

export interface ConfirmationsPayload {
  einwilligungen?: string[]; // digitalProcessing, optionalProcessing
  bestaetigungen?: {}; // e.g. age: 18
  rueckkanal?: {}; // type: account | post
}

@Component({
    tag: "lb-confirmations",
    styleUrl: "styles.scss",
    shadow: true,
})
export class Confirmations {
    @Prop({ mutable: true }) confirmations: ConfirmationsPayload = {};

    updateConfirmations(type, key?, value?) {
      let confirmations = JSON.parse(JSON.stringify(this.confirmations));

      if (type === "bestaetigungen" || type === "rueckkanal") {
        if (this.confirmations?.[type]?.[key] === value) {
          return;
        }

        if (value === null) {
          if (confirmations[type]) {
            delete confirmations[type][key];
          }
        } else {
          const confirmation = {};
          confirmation[key] = value;

          if (!confirmations[type]) {
            confirmations[type] = {};
          }

          confirmations[type] = {...confirmations[type], ...confirmation};
        }
      }

      if (type === "einwilligungen") {
        _debug("type einwilliguungen");
        if (value === null) {
          if (confirmations[type]) {
            const index = confirmations[type].indexOf(key);
            if (index !== -1) {
              confirmations[type]?.splice(index, 1);
            }
          }
        } else {
          if (!confirmations[type]) {
            confirmations[type] = [];
          }
          if (!confirmations[type].includes(key)) {
            confirmations[type].push(key);
          }
        }
      }

      if (type === "anlagenverzeichnis") {
        if (this.confirmations?.[type]?.[key] === value) {
          return;
        }

        if (value === null || (value != null && value.length === 0)) {
          if (confirmations[type]) {
            delete confirmations[type][key];
          }
        } else {
          const confirmation = {};
          confirmation[key] = value;

          if (!confirmations[type]) {
            confirmations[type] = {};
          }
          confirmations[type] = {...confirmations[type], ...confirmation};
        }
      }

      if (JSON.stringify(confirmations) !== JSON.stringify(this.confirmations)) {
        this.confirmations = confirmations;
      }

    }

  @Watch('confirmations')
  onConfirmationsChange() {
    this.confirmationschanged.emit(this.confirmations)
  }

  @Prop() metadata; // "leistung" features
  @Prop() userdata; // user infos
  @Prop() userdecisions; // user decisions
  @Prop() formValid; // is form valid
  @Prop() appConfig;
  @Prop({ reflect: true }) language: string = "de";

  @Watch('formValid')
  handleValidChange(valid) {
    _debug('formValid changed in confirmations')
    _debug(valid)
  }

  @Prop() ageInfo; // age info
  @Prop() formHasOptionalData; // optional da given in form

  @Prop({mutable: true}) data: object; // confirmations
  @Prop() attachmentIndex: object;

  /**
   * "Age Confirmation"
   *
   * https://partner.bdr.de/jira/browse/BSE-23053
   */

  needsAgeConfirmation() {
    _debug("needsAgeConfirmation() entered");
    if (this.userdata) {
      return false; // logged in doesn't need
    }
    if (!this.metadata?.mindestalter) {
      return false; // forms without age requirement don't need it
    }
    return !this.hasBirthdayField();
  }

  needsAgeCheck() {
    _debug("needsAgeCheck() entered");
    if (!this.metadata?.mindestalter) {
      return false;  // forms without age requirement don't need it
    }

    if (this.userdata?.providerName && this.userdata?.providerName !== 'BundID') {
      return false; // org session doesn't need it
    }
    return this.userdata || this.hasBirthdayField();
  }

  isMinAgeReached() {
    return this.getAge() >= parseInt(this.metadata?.mindestalter);
  }

  getAge() {
    let userBirthday = this.getUserBirthday();
    if (!userBirthday) {
      return null;
    }

    const today = new Date();
    const birthDate = new Date(userBirthday);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }

  getUserBirthday() {
    let userBirthday = this.userdata?.geburtsdatum?.wert;

    if (userBirthday) {
      return userBirthday;
    }

    if (this.isBirthdayByFormularProvided()) {
      return this.ageInfo.birthdate;
    }
    return null;
  }

  hasBirthdayField() {
    return this.ageInfo?.hasBirthdate;
  }

  isBirthdayByFormularProvided() {
    if (this.hasBirthdayField()) {
      let formInput = this.ageInfo;
      _debug("isBirthdayByFormularProvided() age form input:", formInput);
      return formInput.birthdate;
    }
    return false;
  }

  updateAgeConfirmation(value?) {
    _debug("updateAgeConfirmation() entered", value);
    const type = "bestaetigungen";
    const key = "age";

    // check if age confirmation is required by "leistung" features (aka metadata)
    if (!this.needsAgeConfirmation()) {
      return;
    }

    if (value === false) {
      this.updateConfirmations(type, key, null);
      this.validate();
      return;
    }

    // check if age confirmation is already given by data or parameter
    let confirmationGiven = value === true || !!this.data?.[type]?.[key];

    if (confirmationGiven) {
      this.updateConfirmations(type, "age", this.metadata.mindestalter);
    }

    this.validate();
  }

    /**
     * Confirmation to process optional given data
     *
     * https://partner.bdr.de/jira/browse/BSE-23054
     */

    updateOptionalDataProcessingConfirmation(value?) {
        // check if optional data was given in form
        if (!this.formHasOptionalData) return;

        this.updateEinwilligungen("optionalProcessing", value);
    }

    /**
     * Change and/or confirm preferred feedback channel
     *
     * https://partner.bdr.de/jira/browse/BSE-23055
     *
     * if "bescheid_schriftform" is set in metadata
     * show Notice regard written form:
     * https://partner.bdr.de/jira/browse/OZGF-1112
     * https://partner.bdr.de/jira/browse/BSE-23056
     */

    updateFeedbackChannelConfirmation(value?) {
      const type = "rueckkanal";
      const key = "type";

      // check if confirmation is already given by data
      let selectedType = value || this.data?.[type]?.[key];

      // ToDo: "post" variabel machen:
      if (selectedType === "post") {
        this.updateConfirmations("einwilligungen", "digitalProcessing", null);
      }

      // update confirmation status
      if (selectedType === "post" || selectedType === "account") {
        this.updateConfirmations(type, key, selectedType);
      }
      this.validate();
    }

    /**
     * Confirmation to digitally process given data
     *
     * https://partner.bdr.de/jira/browse/BSE-23055
     */

    updateDigitalProcessingConfirmation(value?) {
      this.updateEinwilligungen("digitalProcessing", value);
      return;
    }

    /**
     * Confirmation to digitally receiving antrag pdf
     *
     * https://partner.bdr.de/jira/browse/BSE-29276
     */

    updateSendAntragToAccountConfirmation(value?) {
      this.updateEinwilligungen("sendAntragToAccount", value);
      return;
    }

    updateEinwilligungen(key, value?) {
      const type = "einwilligungen";

      if(!key){
        _debug("no key provided -> leaving without update");
        return;
      }

      if (value === false) {
        this.updateConfirmations(type, key, null);
        this.validate();
        return;
      }

      // check if confirmation is already given
      let confirmationGiven = value === true || this.data?.[type]?.includes(key);

      // update confirmation status
      if (confirmationGiven) {
        _debug("confirmationGiven in updateEinwilligungen for ", key);
        this.updateConfirmations(type, key);
      }
      this.validate();
    }

  @Watch("metadata")
  isUserDecisionForRueckkanalRequired(metadata?) {
    let currentMetaData = metadata || this.metadata;
    if (currentMetaData) {
      return currentMetaData.rueckkanalwahl && currentMetaData.rueckkanaloption.length > 1 &&
        this.userdata && !this.userdata.temporaryAccount?.wert && !this.userdecisions?.offline;
    }
    return false;
  }

  @Watch("metadata")
  determinePostAsDefaultRueckkanal(metadata?) {
    let currentMetaData = metadata || this.metadata;
    if (currentMetaData.rueckkanalwahl && this.isRueckkanalTypeProvided('post')) {
      if ((currentMetaData.rueckkanaloption.length === 1) || !this.userdata || this.userdata?.temporaryAccount?.wert ||
        this.userdecisions?.offline) {
        this.updateConfirmations('rueckkanal', 'type', 'post');
        return true;
      }
    }
    return false;
  }

  /**
   * Select and confirm attachments items
   */
  @State() selectedAttachments = []; // vom Nutzer ausgewählte Anhänge

  setSelectedAttachments(attachmentName, isSelected) {
    let attachments = this.selectedAttachments.map((x) => x);// neues array erzeugen, damit der watcher anspringt
    if (isSelected) {
      attachments.push(attachmentName);
    } else {
      if (attachments.includes(attachmentName)) {
        const index = attachments.indexOf(attachmentName);
        attachments.splice(index, 1);
      }
    }
    this.selectedAttachments = this.sortSelectedAttachmentsByOriginalOrder(attachments);
  }

  sortSelectedAttachmentsByOriginalOrder(attachments) {
    if (attachments.length === 0) {
      return [];
    }

    let sortedAttachments = [];
    const optionalAttachments = this.attachmentIndex?.['optional'];
    // iteriere durch die ursprüngliche Attachments, um die Sortierung beizubehalten
    optionalAttachments.forEach((attachment) => {
      let optAttachmentName = attachment.value;
      if (attachments.includes(optAttachmentName)) {
        sortedAttachments.push(optAttachmentName);
      }
    });
    return sortedAttachments;
  }

  updateAttachmentIndex() {
    if (!this.userdecisions.offline) {
      return;
    }
    
    if (this.attachmentIndex?.['required']) {
      let attachmentNamen = [];
      for (const attachment of this.attachmentIndex?.['required']) {
        attachmentNamen.push(attachment.value);
      }
      this.updateConfirmations('anlagenverzeichnis', 'required', attachmentNamen);
    }

    if (this.selectedAttachments) {
      this.updateConfirmations('anlagenverzeichnis', 'optional', this.selectedAttachments);
    }
  }

  validate() {
    _debug('lb-confirmations.validate() start')

    this.errors = []

    if (this.needsAgeCheck() && !this.isMinAgeReached()) {
      this.errors.push(this.i18n?.meta?.[this.language]?.beantragung?.errorAgeInsufficient)
    }

    if (this.needsAgeConfirmation() && !this.confirmations?.bestaetigungen?.["age"]) {
      this.errors.push(this.i18n?.meta?.[this.language]?.beantragung?.errorAgeConfirmationRequired)
    }

    if (
      this.formHasOptionalData &&
      !this.confirmations?.["einwilligungen"]?.includes("optionalProcessing")
    ) {
      this.errors.push(this.i18n?.meta?.[this.language]?.beantragung?.errorOptionalApprovalRequired)
    }

    if (
      this.confirmations.rueckkanal?.["type"] === "account" &&
      !this.userdecisions?.offline &&
      this.userdata?.digitalerRueckkanal &&
      !this.confirmations?.["einwilligungen"]?.includes("digitalProcessing")
    ) {
      this.errors.push(this.i18n?.meta?.[this.language]?.beantragung?.errorDigitalProcessingRequired)
    }

    _debug('lb-confirmations.validate() end, errors', this.errors);
  }

  @State() confirmationsValidated: boolean;
  @State() defaultFeedbackChannelDetermined: boolean;

    /**
     * Eventemitter "confirmationsvalidated"
     *
     */
    @Event() confirmationsvalidated: EventEmitter<object>;

     /**
     * Eventemitter "confirmationschanged"
     *
     */
    @Event() confirmationschanged: EventEmitter<object>;

    @State() errors: string[] = []

    @State() showErrors = false

    confirmValidation(args) {

      this.validate();

      if(this.errors.length > 0 ) { 
        this.showErrors = true
        return
      }  

      if(!this.formValid) {
        return
      }

      if (this.metadata?.willensbekundung && !this.userdecisions.offline && args.source === 'initial') {
        this.confirmationDialog = true;
      } else {
       this.confirmationDialog = false;
       this.confirmationsvalidated.emit(this.confirmations);
      }
    }

  // check if all required confirmations are given

  @Watch("metadata")
  @Watch("userdata")
  @Watch("userdecisions")
  @Watch("data")
  @Watch("attachmentIndex")
  @Watch("selectedAttachments")
  checkConfirmations(data?) {
    if(data && data.einwilligungen && (JSON.stringify(data) === JSON.stringify(this.data))) {
        return;
    }
    if (!this.metadata) {
      return;
    }
    this.updateAgeConfirmation();
    this.updateOptionalDataProcessingConfirmation();
    this.updateFeedbackChannelConfirmation();
    this.updateDigitalProcessingConfirmation();
    this.updateSendAntragToAccountConfirmation();
    this.updateAttachmentIndex();
  }

  @State() confirmationDialog: boolean;

  @Prop() i18n;

  isRueckkanalTypeProvided(rueckkanalType) {
    return this.metadata.rueckkanaloption?.includes(rueckkanalType);
  }

  setDefaultFeedbackChannel() {
    let rueckkanalType = null;

    if (this.isUserDecisionForRueckkanalRequired()) {
      if (this.metadata.rueckkanaloption?.includes('account')) {
        rueckkanalType = 'account';
      } else if (this.metadata.rueckkanaloption?.includes('post')) {
        rueckkanalType = 'post';
      }
    }

    if (rueckkanalType !== null) {
      const defaultFeedbackChannel = {type: rueckkanalType};
      if (!this.confirmations) {
        this.confirmations = {rueckkanal: defaultFeedbackChannel};
      } else if (!this.confirmations['rueckkanal']) {
        this.confirmations['rueckkanal'] = defaultFeedbackChannel;
      }
      this.defaultFeedbackChannelDetermined = true;
    }
  }

  componentDidLoad() {
    this.checkConfirmations();
  }

  componentWillLoad() {
    this.setDefaultFeedbackChannel();
  }

  private areAttachmentsAvailable() {
    return this.attachmentIndex && (
      (this.attachmentIndex['required'] && this.attachmentIndex['required'].length > 0) ||
      (this.attachmentIndex['optional'] && this.attachmentIndex['optional'].length > 0)
    );
  }

  render() {
    return (
      <Host>

        <div class="confirmation-wrapper">
          <c-form>
            {this.needsAgeConfirmation() && (
              <Fragment>
                <div>
                  <span class="label-like">{this.i18n?.meta?.[this.language]?.behoerdenTexte?.ageCheckboxHeadline}</span>
                  <muse-checkbox name="age" 
                      checked={!!this.confirmations?.bestaetigungen?.["age"]}
                      data-testid="confirmation-age-checkbox"
                      onChange={(evt) => {
                        _debug("confirmation-age-checkbox changed to:", evt.detail['checked']);
                          this.updateAgeConfirmation(evt.detail['checked']);
                        }} variant="font-body-md">
                      <span slot="label">
                          {replaceText(this.i18n?.meta?.[this.language]?.behoerdenTexte?.ageCheckboxLabel, 'minAge',
                          this.metadata?.mindestalter)}
                      </span>
                  </muse-checkbox>                 
                </div>
              </Fragment>
            )}

            {this.userdata?.digitalerRueckkanal && (
              <Fragment>
                <div>
                  <span class="label-like">{this.i18n?.meta?.[this.language]?.beantragung?.sendAntragToAccountConfirmationHeadline}</span>
                  <muse-checkbox name="send-to-account" 
                    checked={this.confirmations?.einwilligungen?.includes("sendAntragToAccount")}                                   
                    data-testid="confirmation-send-antrag-to-account-checkbox"
                    onChange={(evt) => {
                      _debug("confirmation-send-antrag-to-account-checkboxx changed to:", evt.detail['checked']);
                      this.updateSendAntragToAccountConfirmation(evt.detail['checked']);
                      }} variant="font-body-sm">
                    <span slot="label">{this.i18n?.meta?.[this.language]?.beantragung?.sendAntragToAccountConfirmationText}
                    </span>
                  </muse-checkbox> 
                 
                </div>
              </Fragment>
            )}

            {this.needsAgeCheck() && !this.isMinAgeReached() && (              
              <div class="error-body" data-testid="insufficient-age-infobox">
                {replaceText(this.i18n?.meta?.[this.language]?.behoerdenTexte?.ageInfobox, 'minAge', this.metadata?.mindestalter)}
              </div>
            )}

            {this.formHasOptionalData && (
              <Fragment>
                <div>
                  <span class="label-like">{this.i18n?.meta?.[this.language]?.behoerdenTexte?.approvalCheckboxHeadline}</span>
                  <muse-checkbox name="optional-data" 
                    data-testid="confirmation-send-optional-data-checkbox"
                    checked={this.confirmations?.einwilligungen?.includes("optionalProcessing")}
                    onChange={(evt) => {
                      _debug("confirmation-send-optional-data-checkbox changed to:", evt.detail['checked'])
                      this.updateOptionalDataProcessingConfirmation(evt.detail['checked'])
                      }} variant="font-body-sm">
                    <span slot="label">{this.i18n?.meta?.[this.language]?.behoerdenTexte?.approvalCheckboxLabel}
                    </span>
                  </muse-checkbox>                  
                </div>
                <c-markdown-renderer>
                  <div slot="source">
                    {this.i18n?.meta?.[this.language]?.behoerdenTexte?.approvalCheckboxInfo}
                  </div>
                </c-markdown-renderer>
              </Fragment>
            )}

            {this.isUserDecisionForRueckkanalRequired() && this.defaultFeedbackChannelDetermined && (
              <div>
                <span class="label-like">{ this.i18n?.meta?.[this.language]?.behoerdenTexte?.feedbackChannelLabel }</span>
                <a-radiogroup
                 data-testid="confirmation-feedback-channel-radio"
                 onValuechange={({detail}) => {
                     _debug("confirmation-feedback-channel-radio changed to:", detail);
                     this.updateFeedbackChannelConfirmation(detail);
                  }}
                  value={this.confirmations?.["rueckkanal"]?.["type"]}
                  data={[
                    this.metadata.rueckkanaloption[0],
                    this.metadata.rueckkanaloption[1],
                  ]}
                  labels={{
                    [this.metadata.rueckkanaloption[0]]: this.i18n?.meta?.[this.language]?.behoerdenTexte?.feedbackChannelAccount,
                    [this.metadata.rueckkanaloption[1]]: this.i18n?.meta?.[this.language]?.behoerdenTexte?.feedbackChannelPost,
                  }}
                ></a-radiogroup>

                {this.confirmations?.["rueckkanal"]?.["type"] === "account" && (
                  <Fragment>
                  
                    <c-markdown-renderer>
                      <div slot="source">
                        {replaceText(this.i18n?.meta?.[this.language]?.behoerdenTexte?.digitalFeedbackInfo, 'behoerde',
                          this.metadata?.behoerdenName)}
                      </div>
                    </c-markdown-renderer>

                    <muse-checkbox name="digital-processing" 
                      data-testid="confirmation-digital-processing-checkbox"
                      checked={this.confirmations?.einwilligungen?.includes("digitalProcessing")}
                      onChange={(evt) => {
                          this.updateDigitalProcessingConfirmation(evt.detail['checked'])
                        }} 
                      variant="font-body-sm">
                      <span slot="label">{this.i18n?.meta?.[this.language]?.behoerdenTexte?.digitalFeedbackCheck}
                      </span>
                    </muse-checkbox>

                    {this.metadata.bescheid_schriftform && (                     
                      <c-markdown-renderer>
                        <div slot="source">
                          {this.i18n?.meta?.[this.language]?.behoerdenTexte?.schriftformInfo}
                        </div>
                      </c-markdown-renderer>
                    )}
                  </Fragment>
                )}
              </div>
            )}

            {this.determinePostAsDefaultRueckkanal() && (
              <c-panel>
                <c-text>{this.i18n?.meta?.[this.language]?.behoerdenTexte?.feedbackInfoPost}</c-text>
              </c-panel>
            )}

            {this.userdecisions.offline && this.areAttachmentsAvailable() && (
              <div>
                <span class="label-like">{this.i18n?.meta?.[this.language]?.beantragung?.attachmentIndexRequiredHeadline}</span>
                <ul data-testid="confirmation-optional-attachments-list">
                {this.attachmentIndex['required']?.map((requiredAttachment) => {
                  return (
                    <li>{ requiredAttachment.label }</li>
                  )
                })}
                </ul>
                <span class="label-like">{this.i18n?.meta?.[this.language]?.beantragung?.attachmentIndexOptionalHeadline}</span>
                <div data-testid="confirmation-optional-attachments-checkboxes">
                  {this.attachmentIndex['optional']?.map((optionalAttachment) => {
                    return (
                      <div>                       
                        <muse-checkbox name={ 'optional-attachments_'+ optionalAttachment.value}
                          onChange={(evt) => {
                            _debug("confirmation-digital-processing-checkbox to:", evt.detail['checked'])
                            this.setSelectedAttachments(optionalAttachment.value, evt.detail['checked'])
                            }} variant="font-body-sm">
                          <span slot="label">{optionalAttachment.label}
                          </span>
                        </muse-checkbox>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </c-form>

          {
            !this.formValid &&
              <muse-status-message variant="error" icon-name="alert-triangle" role="alertdialog">
                {this.i18n?.meta?.[this.language]?.beantragung?.errorFormDataIncorrect}
              </muse-status-message>
          }

          {
            this.showErrors && this.errors.length > 0 && this.errors.map((error) => (
              <muse-status-message variant="error" icon-name="alert-triangle" role="alertdialog">
                { error }
              </muse-status-message>
            ))
          }

          <div>
            <muse-button primary 
                      data-testid="send-antrag-button"
                      style={{  float: 'right', marginTop: '16px', marginLeft: '16px'}}
                      onClick={() => this.confirmValidation({source: 'initial'})}>
              {(!this.userdecisions.offline) && (
                this.i18n?.meta?.[this.language]?.behoerdenTexte?.sendButton
              )}
              {(this.userdecisions.offline) && (
                this.i18n?.meta?.[this.language]?.behoerdenTexte?.offlineButton
              )}
            </muse-button>
          </div>

          { // show "Do you really want to send it" dialog
            this.confirmationDialog &&
            <muse-dialog open onDismiss={ ()=> this.confirmationDialog = false }>
              <c-header type="h2" role="heading">
                {this.i18n?.meta?.[this.language]?.behoerdenTexte?.intentHeadline}
              </c-header>
              <c-markdown-renderer>
                <div slot="source">
                  {this.i18n?.meta?.[this.language]?.behoerdenTexte?.intentText}
                </div>
              </c-markdown-renderer>
              <br/>
              <br/>

              <div class="confirm-buttons">
                <c-button
                  centered-content
                  block
                  large
                  onClick={() => {
                    this.confirmationDialog = false;
                  }}
                ><span>{this.i18n?.meta?.[this.language]?.behoerdenTexte?.intentDenyBtnLabel}</span>
                </c-button>
                <br/>
                <c-button
                  primary
                  centered-content
                  block
                  large
                  onClick={() => {
                    this.confirmValidation({source: 'intentdialog'})
                  }}
                ><span>{this.i18n?.meta?.[this.language]?.behoerdenTexte?.intentAcceptBtnLabel}</span>
                </c-button>
              </div>
            </muse-dialog>
          }

        </div>
      </Host>
    );
  }
}
